import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { ArtistDemand, ArtworkDemand, ArtistSpotlight, Tag } from '../types';
import { API_BASE } from '../services/api';
import OptimizedImage from '../components/OptimizedImage';
import './Shop.css';

const Shop: React.FC = () => {
  const navigate = useNavigate();
  const [topArtists, setTopArtists] = useState<ArtistDemand[]>([]);
  const [topPieces, setTopPieces] = useState<ArtworkDemand[]>([]);
  const [artistSpotlight, setArtistSpotlight] = useState<ArtistSpotlight | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (): Promise<void> => {
    setLoading(true);
    try {
      const [demandResponse, spotlightResponse, tagsResponse] = await Promise.all([
        api.get<{ topArtists: ArtistDemand[]; topPieces: ArtworkDemand[] }>('/art/demand', {
          params: { top: 20 }
        }),
        api.get<ArtistSpotlight>('/art/artist-of-month').catch(() => ({ data: null })),
        api.get<Tag[]>('/tags')
      ]);

      setTopArtists(demandResponse.data.topArtists || []);
      setTopPieces(demandResponse.data.topPieces || []);
      setArtistSpotlight(spotlightResponse.data);
      setTags(tagsResponse.data);
    } catch (error) {
      console.error('Failed to fetch shop data:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildImageSrc = (imageUrl?: string): string | null => {
    if (!imageUrl) return null;
    return imageUrl.startsWith('http') ? imageUrl : `${API_BASE}${imageUrl}`;
  };

  const handleArtistClick = (artist: ArtistDemand): void => {
    if (artist.artistId) {
      navigate(`/artists/${artist.artistId}/shop`);
    } else {
      const artistKey = `name:${artist.artistName || 'Unknown Artist'}`;
      navigate(`/art/browse?artist=${encodeURIComponent(artistKey)}`);
    }
  };

  const handleArtworkClick = (piece: ArtworkDemand): void => {
    navigate(`/artworks/${piece.artworkId}`);
  };

  const handleTagClick = (tag: Tag): void => {
    navigate(`/art/browse?tag=${tag.id}`);
  };

  const handleViewAll = (type: 'artists' | 'artworks' | 'tags', tagId?: number): void => {
    if (type === 'artists') {
      navigate('/art/browse?view=artists');
    } else if (type === 'artworks') {
      navigate('/art/browse');
    } else if (type === 'tags' && tagId) {
      navigate(`/art/browse?tag=${tagId}`);
    }
  };

  if (loading) {
    return (
      <div className="shop-page">
        <div className="loading">Loading shop...</div>
      </div>
    );
  }

  // Group tags by type for category carousel
  const tagsByType = tags.reduce((acc, tag) => {
    if (!acc[tag.type || 'Other']) {
      acc[tag.type || 'Other'] = [];
    }
    acc[tag.type || 'Other'].push(tag);
    return acc;
  }, {} as Record<string, Tag[]>);

  return (
    <div className="shop-page">
      {/* Hero Section */}
      <section className="shop-hero">
        <div className="shop-hero-content">
          <h1 className="shop-title">Shop Art</h1>
          <p className="shop-subtitle">Discover unique artwork from talented artists</p>
        </div>
      </section>

      {/* Artist Spotlight */}
      {artistSpotlight && (
        <section className="shop-section shop-spotlight">
          <div className="section-header">
            <h2 className="section-title">Artist of the Month</h2>
          </div>
          <div className="spotlight-card" onClick={() => {
            if (artistSpotlight.artistId || artistSpotlight.artistName) {
              handleArtistClick({
                artistId: artistSpotlight.artistId,
                artistName: artistSpotlight.artistName || 'Unknown Artist',
                demandScore: 0,
                imageUrl: undefined,
                totalQuantity: 0,
                orderCount: 0,
                cartCount: 0,
                viewCount: 0
              });
            }
          }}>
            <div className="spotlight-info">
              <h3>{artistSpotlight.artistName || 'Featured Artist'}</h3>
              <p>Artist of the Month - {new Date(artistSpotlight.year, artistSpotlight.month - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}</p>
            </div>
          </div>
        </section>
      )}

      {/* Top Artists Carousel */}
      {topArtists.length > 0 && (
        <section className="shop-section">
          <div className="section-header">
            <h2 className="section-title">Featured Artists</h2>
            <button
              className="section-view-all"
              onClick={() => handleViewAll('artists')}
            >
              View All Artists
            </button>
          </div>
          <div className="carousel-container">
            <div className="carousel-track">
              {topArtists.slice(0, 12).map((artist, index) => {
                const imageSrc = buildImageSrc(artist.imageUrl);
                return (
                  <div
                    key={`${artist.artistId || artist.artistName}-${index}`}
                    className="carousel-item artist-card"
                    onClick={() => handleArtistClick(artist)}
                  >
                    {imageSrc ? (
                      <div className="carousel-item-image">
                        <img
                          src={imageSrc}
                          alt={artist.artistName}
                          loading="lazy"
                        />
                      </div>
                    ) : (
                      <div className="carousel-item-placeholder">
                        <i className="fas fa-user"></i>
                      </div>
                    )}
                    <div className="carousel-item-label">{artist.artistName}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Top Pieces Carousel */}
      {topPieces.length > 0 && (
        <section className="shop-section">
          <div className="section-header">
            <h2 className="section-title">Popular Artwork</h2>
            <button
              className="section-view-all"
              onClick={() => handleViewAll('artworks')}
            >
              View All Artwork
            </button>
          </div>
          <div className="carousel-container">
            <div className="carousel-track">
              {topPieces.slice(0, 12).map((piece, index) => {
                const imageSrc = buildImageSrc(piece.imageUrl);
                return (
                  <div
                    key={`${piece.artworkId}-${index}`}
                    className="carousel-item artwork-card"
                    onClick={() => handleArtworkClick(piece)}
                  >
                    {imageSrc && piece.imageUrl ? (
                      <div className="carousel-item-image">
                        <OptimizedImage
                          src={piece.imageUrl}
                          thumbnailSrc={(piece as any).thumbnailUrl} // Use thumbnail if available
                          alt={piece.title || 'Artwork'}
                          priority={index < 6} // First 6 are above the fold
                        />
                      </div>
                    ) : (
                      <div className="carousel-item-placeholder">
                        <i className="fas fa-image"></i>
                      </div>
                    )}
                    <div className="carousel-item-label">{piece.title}</div>
                    {piece.artistName && (
                      <div className="carousel-item-sublabel">by {piece.artistName}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Categories by Tag Type */}
      {Object.keys(tagsByType).length > 0 && (
        <section className="shop-section">
          <div className="section-header">
            <h2 className="section-title">Browse by Category</h2>
          </div>
          <div className="categories-grid">
            {Object.entries(tagsByType).map(([type, typeTags]) => (
              <div key={type} className="category-group">
                <h3 className="category-type-title">{type}</h3>
                <div className="category-tags">
                  {typeTags.slice(0, 8).map((tag) => (
                    <button
                      key={tag.id}
                      className="category-tag"
                      onClick={() => handleTagClick(tag)}
                    >
                      {tag.name}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Browse All CTA */}
      <section className="shop-section shop-cta">
        <div className="cta-content">
          <h2>Explore Our Full Collection</h2>
          <p>Browse thousands of unique artworks from talented artists</p>
          <button
            className="btn btn-primary btn-large"
            onClick={() => navigate('/art/browse')}
          >
            Browse All Artwork
          </button>
        </div>
      </section>
    </div>
  );
};

export default Shop;
